import React, { useState, useEffect } from "react";
import { Navigate, Link } from 'react-router-dom'
import Cookies from 'universal-cookie';
import '../index.css';
import logo from '../assets/spinner.gif'
import { populateTicketList, GetAttributes, UseLocalStorage, GetAttributesByAssignment, GetColumnHeaders } from "../functions";

// Helper function to calculate total price
const calculateTotalPrice = (ticket) => {
  const price = parseFloat(ticket.price) || 0;
  const quantity = parseInt(ticket.quantity) || 1;
  const shippingfee = parseFloat(ticket.shippingfee) || 0;
  const otherfees = parseFloat(ticket.otherfees) || 0;

  return (price * quantity - shippingfee - otherfees).toFixed(2);
};

function Home() {
  const [localStorageAttributes, setLocalStorageAttributes] = UseLocalStorage('attributes', []);
  const [localStorageTickets, setLocalStorageTickets] = UseLocalStorage('tickets', []);
  const [localAppVersion, setLocalAppVersion] = UseLocalStorage('appversion', '0');

  const [pageTitle, setPageTitle] = UseLocalStorage('pageTitle', 'Tickets');
  const [pageAttr, setPageAttr] = UseLocalStorage('pageAttr', 'Ticket');
  const [columnHeaders, setColumnHeaders] = UseLocalStorage('columnHeaders', ['one', 'two']);

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  const [loading, setLoading] = useState(true);

  const cookies = new Cookies();
  let login = cookies.get('login');
  console.log('homepage running appversion: ' + process.env.REACT_APP_VERSION);

  useEffect(() => {
    if (login) {
      const loadPost = async () => {
        console.log('Starting home load');
        setLoading(true);

        let attributes = [];
        if (localStorageAttributes != null && localStorageAttributes.length > 0 && localAppVersion === process.env.REACT_APP_VERSION) {
          attributes = localStorageAttributes;
        } else {
          attributes = await GetAttributes();
          setLocalStorageAttributes(attributes);
          setLocalAppVersion(process.env.REACT_APP_VERSION);
          console.log('fresh load of attributes complete');
        }

        const ticketAttributes = GetAttributesByAssignment('tickets', attributes);
        setPageTitle(ticketAttributes.displayplural);
        setPageAttr(ticketAttributes.displayname);

        setColumnHeaders(GetColumnHeaders(ticketAttributes.tags.sort((a, b) => a.displayorder.localeCompare(b.displayorder))));

        let tickets = await populateTicketList();

        // Custom status sorting: pending -> in progress -> completed -> others
        const statusOrder = {
          'pending': 1,
          'inprogress': 2,
          'completed': 3,
          'cancelled': 4
        };

        tickets.sort((a, b) => {
          const statusA = (a.ticketstatus || '').toLowerCase();
          const statusB = (b.ticketstatus || '').toLowerCase();

          const orderA = statusOrder[statusA] || 999; // Unknown statuses go to end
          const orderB = statusOrder[statusB] || 999;

          // Primary sort by status
          if (orderA !== orderB) {
            return orderA - orderB;
          }

          // Secondary sort by order date DESC (newest first within same status)
          return b.orderdate.localeCompare(a.orderdate);
        });

        setLocalStorageTickets(tickets);
        setFilteredResults(tickets);
        setLoading(false);
        console.log('Home load complete');
      };
      loadPost();
    }
  }, [])

  if (!login) {
    return (<Navigate to={{ pathname: '../Login' }} />);
  }

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching

    let results;
    if (!query.trim()) {
      results = localStorageTickets;
    } else {
      // Parse search terms, handling quoted phrases
      const parseSearchTerms = (searchQuery) => {
        const terms = [];
        const regex = /"([^"]+)"|(\S+)/g;
        let match;

        while ((match = regex.exec(searchQuery)) !== null) {
          // match[1] is quoted phrase, match[2] is individual word
          terms.push(match[1] || match[2]);
        }

        return terms;
      };

      const searchTerms = parseSearchTerms(query.trim());

      // Filter logic - ticket must match ALL search terms
      results = localStorageTickets.filter(t => {
        // Create a searchable string from all ticket fields
        const searchableText = [
          t.id,
          t.customer,
          t.orderdate?.toString(),
          t.duedate?.toString(),
          t.paymentmethod?.toString(),
          t.ticketstatus?.toString(),
          t.product?.toString()
        ].filter(Boolean).join(' ').toLowerCase();

        // Check if all search terms are found in the searchable text
        return searchTerms.every(term => searchableText.includes(term));
      });
    }

    // Apply current sorting if any
    if (sortColumn) {
      results = [...results].sort((a, b) => {
        let aValue = a[sortColumn] || '';
        let bValue = b[sortColumn] || '';
        
        // Handle special cases
        if (sortColumn === 'price') {
          aValue = parseFloat(calculateTotalPrice(a)) || 0;
          bValue = parseFloat(calculateTotalPrice(b)) || 0;
        } else if (sortColumn === 'orderdate' || sortColumn === 'duedate') {
          aValue = new Date(aValue || '1900-01-01');
          bValue = new Date(bValue || '1900-01-01');
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    }

    setFilteredResults(results);
  };

  // Sorting function
  const handleSort = (columnTag) => {
    let direction = 'asc';
    if (sortColumn === columnTag && sortDirection === 'asc') {
      direction = 'desc';
    }
    
    setSortColumn(columnTag);
    setSortDirection(direction);
    
    const sortedResults = [...filteredResults].sort((a, b) => {
      let aValue = a[columnTag] || '';
      let bValue = b[columnTag] || '';
      
      // Handle special cases
      if (columnTag === 'price') {
        aValue = parseFloat(calculateTotalPrice(a)) || 0;
        bValue = parseFloat(calculateTotalPrice(b)) || 0;
      } else if (columnTag === 'orderdate' || columnTag === 'duedate') {
        aValue = new Date(aValue || '1900-01-01');
        bValue = new Date(bValue || '1900-01-01');
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
    
    setFilteredResults(sortedResults);
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResults = filteredResults.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrint = () => {
    const totalValue = filteredResults.filter(ticket => (ticket.ticketstatus || '').toLowerCase() !== 'cancelled').reduce((total, ticket) => total + parseFloat(calculateTotalPrice(ticket)), 0).toFixed(2);
    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    // Create print content
    let printContent = `
      <html>
        <head>
          <title>${pageTitle} Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #000; 
              background: #fff;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .header h1 { 
              margin: 0; 
              color: #333; 
              font-size: 28px;
            }
            .report-info { 
              display: flex; 
              justify-content: space-between; 
              margin: 15px 0;
              font-size: 14px;
              color: #666;
            }
            .summary { 
              background: #f5f5f5; 
              padding: 15px; 
              margin: 20px 0; 
              border-radius: 5px;
              border: 1px solid #ddd;
            }
            .summary-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 8px 0;
              font-weight: bold;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px;
              font-size: 12px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
              color: #333;
            }
            tr:nth-child(even) { 
              background-color: #f9f9f9; 
            }
            .search-info {
              background: #e8f4f8;
              padding: 10px;
              margin: 15px 0;
              border-radius: 5px;
              border-left: 4px solid #2196F3;
              font-style: italic;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${pageTitle} Report</h1>
            <div class="report-info">
              <span>Generated: ${currentDate} at ${currentTime}</span>
              <span>Total Records: ${filteredResults.length}</span>
            </div>
          </div>
          
          ${searchQuery ? `
            <div class="search-info">
              <strong>Search Filter Applied:</strong> "${searchQuery}"
            </div>
          ` : ''}
          
          <div class="summary">
            <div class="summary-row">
              <span>Total ${pageTitle}:</span>
              <span>${filteredResults.length}</span>
            </div>
            <div class="summary-row">
              <span>Total Value:</span>
              <span>$${totalValue}</span>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                ${columnHeaders.filter(c => c.searchable === 'true').map(c => `<th>${c.displayname}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${filteredResults.map(ticket => `
                <tr>
                  ${columnHeaders.filter(c => c.searchable === 'true').map(c => `
                    <td>
                      ${c.tag === 'ticketstatus' && ticket[c.tag] ? ticket[c.tag] :
        c.tag === 'price' ? '$' + calculateTotalPrice(ticket) :
          ticket[c.tag] || ''}
                    </td>
                  `).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    if (currentPage > 1) {
      pageNumbers.push(
        <button key="prev" onClick={() => handlePageChange(currentPage - 1)}>
          Previous
        </button>
      );
    }

    // First page
    if (startPage > 1) {
      pageNumbers.push(
        <button key={1} onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="ellipsis1">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          style={{
            backgroundColor: currentPage === i ? '#444444' : '#333333',
            fontWeight: currentPage === i ? 'bold' : 'normal'
          }}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2">...</span>);
      }
      pageNumbers.push(
        <button key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </button>
      );
    }

    // Next button
    if (currentPage < totalPages) {
      pageNumbers.push(
        <button key="next" onClick={() => handlePageChange(currentPage + 1)}>
          Next
        </button>
      );
    }

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredResults.length)} of {filteredResults.length} results
        </div>
        <div className="pagination-buttons">
          {pageNumbers}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="Home">
        <h1>{pageTitle}</h1>
        <div className="home-stats">
          <div className="stat-item">
            <span className="stat-number">{filteredResults.length}</span>
            <span className="stat-label">Total {pageTitle}</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              ${filteredResults.filter(ticket => (ticket.ticketstatus || '').toLowerCase() !== 'cancelled').reduce((total, ticket) => total + parseFloat(calculateTotalPrice(ticket)), 0).toFixed(2)}
            </span>
            <span className="stat-label">Total Value</span>
          </div>
        </div>
      </div>
      <div className="search-and-actions">
        <div className="left-actions">
        </div>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by ticket#, customer, date, or due date"
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
        </div>
        <div className="right-actions">
          <button onClick={handlePrint} className="print-btn" title="Print current list">
            <span className="btn-icon">🖨️</span>
            Print List
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <img src={logo} style={{ width: '50px' }} alt="loading..." />
          <div className="loading-text">Loading tickets...</div>
        </div>
      ) : (
        <>
          {currentResults.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <h3>No tickets found</h3>
              <p>
                {searchQuery
                  ? `No tickets match your search for "${searchQuery}"`
                  : "No tickets available. Create your first ticket to get started!"
                }
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <div id="ticketList" className="centered-table">
                  <table>
                    <thead>
                      <tr>
                        {columnHeaders.filter(columnHeaders => columnHeaders.searchable === 'true').map((c) => (
                          <th 
                            key={c.tag} 
                            style={{ width: '100px', cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort(c.tag)}
                            title={`Click to sort by ${c.displayname}`}
                          >
                            {c.displayname}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentResults.map((t) => (
                        <tr key={t.id}>
                          {columnHeaders.map((c) => (
                            <td key={c.tag}>
                              <Link to="/TicketMaint" state={t}>
                                {c.tag === 'ticketstatus' && t[c.tag] ? (
                                  <span className={`status-indicator status-${t[c.tag].toLowerCase().replace(/\s+/g, '-')}`}>
                                    {t[c.tag]}
                                  </span>
                                ) : c.tag === 'price' ? (
                                  `$${calculateTotalPrice(t)}`
                                ) : (
                                  t[c.tag]
                                )}
                              </Link>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-view">
                <div className="ticket-cards">
                  {currentResults.map((t) => (
                    <div key={t.id} className="ticket-card">
                      <Link to="/TicketMaint" state={t} className="card-link">
                        <div className="card-header">
                          <span className="ticket-id">#{t.id}</span>
                          <span className="ticket-date">{t.orderdate}</span>
                        </div>
                        <div className="card-body">
                          <div className="card-field">
                            <span className="field-label">Customer:</span>
                            <span className="field-value">{t.customer}</span>
                          </div>
                          {columnHeaders.slice(3).map((c) => (
                            t[c.tag] && (
                              <div key={c.tag} className="card-field">
                                <span className="field-label">{c.displayname}:</span>
                                <span className="field-value">
                                  {c.tag === 'ticketstatus' ? (
                                    <span className={`status-indicator status-${t[c.tag].toLowerCase().replace(/\s+/g, '-')}`}>
                                      {t[c.tag]}
                                    </span>
                                  ) : c.tag === 'price' ? (
                                    `$${calculateTotalPrice(t)}`
                                  ) : (
                                    t[c.tag]
                                  )}
                                </span>
                              </div>
                            )
                          ))}
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {!loading && renderPagination()}
    </>
  );
}

export default Home;
